import { ReactNode, useState, useEffect, useRef } from 'react';
import { Form, FormApi } from 'informed';
import RenderedQuestionInstance from '../../../common/QuoteComponents/RenderedQuestionInstance';
import { NextButton, BackButton } from '../NavButtons';
import {
  QuoteWizardQuestionInstance,
  QuoteWizardForm,
} from '../../../QuoteWizard/classes';
import type { StepWizardChildProps } from 'react-step-wizard';
import {
  withFormStyles,
  WithFormStyles,
} from '../../../../util/withFormStyles';
import { useSelector } from 'react-redux';
import { generateGetAnsweredForm } from '../../../../store/policystreamStore';
import {
  formStateToAnswerInstances,
  answerInstancesToFormState,
} from '../../../QuoteWizard/QuoteForms/utility';
import { useSetPolicyStreamSaveButtonVisibility } from '../../../../hooks';
import { usePolicyStreamSubmitAnswers } from '../../../../hooks/usePolicyStreamSubmitAnswers';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { FormState } from 'informed';
import { PolicyStreamFormCard } from '../PolicyStreamFormCard';
import { FormErrorsWarning } from '../../../QuoteWizard/FormErrorsWarning';
import { InitialValues } from '../../../../Typescript/forms';
import { merge } from 'lodash';

type NavType = undefined | 'forward' | 'backward';
interface PolicystreamFormProps
  extends Partial<StepWizardChildProps>,
    WithFormStyles {
  form: QuoteWizardForm;
  validateForm?: (values: FormState['values']) => undefined | string;
  generateOnFormChange?: (formApi: FormApi) => (state: FormState) => void; //
  onFirstPrev: () => void; // how to navigate back when you're the first component
  onLastNext: () => void; // how to navigate forward when you're the last component
  BackButton?: ReactNode;
  NextButton?: ReactNode;
  initialValues?: InitialValues | null;
}
export const PolicystreamForm = withFormStyles((theme) => {
  return {
    form: {
      width: '760px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: `${theme.spacing(6)} ${theme.spacing(12)}`,
      margin: 'auto',
      '& h6': {
        marginTop: theme.spacing(5),
      },
      maxWidth: '60rem',
      // Smaller Screens
      [theme.breakpoints.down('lg')]: {
        padding: `${theme.spacing(3)} ${theme.spacing(0)}`,
      },
      [theme.breakpoints.only('md')]: {
        padding: `${theme.spacing(3)} ${theme.spacing(12)}`,
        maxWidth: '50rem',
      },
    },
  };
})(
  ({
    form,
    validateForm,
    generateOnFormChange,
    initialValues,
    classes,
    onFirstPrev,
    onLastNext,
    currentStep,
    totalSteps,
    nextStep,
    previousStep,
  }: PolicystreamFormProps) => {
    useSetPolicyStreamSaveButtonVisibility(true);
    useScrollToTopOnInitialMount();
    const formApiRef = useRef<FormApi>();
    const [initializedInitialValues, setInitializedInitialValues] = useState<
      InitialValues | undefined
    >(initialValues ?? undefined);
    const answeredForm = useSelector(generateGetAnsweredForm(form));
    const [requestedNavType, setRequestedNavType] = useState<NavType>();
    const [okToNavigate, setOkToNavigate] = useState<boolean>(false);
    const [initialValuesSet, setInitialValuesSet] = useState<boolean>(false);
    const [formError, setFormError] = useState<string | undefined>(undefined);
    const submitAnswers = usePolicyStreamSubmitAnswers();

    // navigate when we're ready to
    useEffect(() => {
      if (okToNavigate) {
        if (requestedNavType === 'forward') {
          if (totalSteps === undefined) {
            return;
          }
          if (currentStep === totalSteps) {
            setRequestedNavType(undefined);
            onLastNext();
          } else if (nextStep) {
            setRequestedNavType(undefined);
            nextStep();
          }
        } else if (requestedNavType === 'backward') {
          if (currentStep === 1) {
            setRequestedNavType(undefined);
            onFirstPrev();
          } else if (previousStep) {
            setRequestedNavType(undefined);
            previousStep();
          }
        }
      }
    }, [requestedNavType, okToNavigate]);

    // clear the requested nav type whenever we leave
    useEffect(() => {
      return () => {
        setRequestedNavType(undefined);
        setOkToNavigate(false);
      };
    }, []);

    // set the form values in initialValues, once / if we get the values and if the api is ready
    useEffect(() => {
      if (
        !initialValuesSet &&
        (answeredForm ||
          (form.questionInstances && form.questionInstances.length > 0))
      ) {
        const values = merge(
          initialValues == null ? {} : initialValues,
          answerInstancesToFormState(
            answeredForm?.answers,
            form.questionInstances,
          ),
        );

        setInitializedInitialValues(values);
        setInitialValuesSet(true);
      }
    }, [initialValues, initialValuesSet, answeredForm, form.questionInstances]);

    if (
      [currentStep, totalSteps, nextStep, previousStep].some(
        (attr) => attr === undefined,
      )
    ) {
      /* return <p>missing required attr</p>; */
      return null;
    }

    // wait until the initial values are set before rendering the form
    if (!initialValuesSet) {
      return null;
      // <p>
      //   Initial values not set
      //   {/* <PolicyStreamFormCard /> */}
      // </p>
    }

    return (
      <PolicyStreamFormCard>
        <Form
          id='Forms'
          autoComplete='off'
          allowEmptyStrings
          formApiRef={formApiRef}
          initialValues={
            // NB the 'initialValues' prop is sometimes not present on the initial mount, so we explictly set it in a useeffect rather than deal with it here
            // answeredForm
            //   ? answerInstancesToFormState(
            //       answeredForm.answers,
            //       form.questionInstances,
            //     )
            //   : undefined
            // undefined
            initializedInitialValues
          }
          preventEnter
          className={classes.form}
          style={{ width: '100%' }}
          onChange={(formState) => {
            if (generateOnFormChange && formApiRef.current) {
              const instantiated = generateOnFormChange(formApiRef.current);
              instantiated(formState);
            }
          }}
          onSubmit={(formState) => {
            const { values } = formState;
            //console.log({ values });
            const error =
              typeof validateForm === 'function'
                ? validateForm(values)
                : undefined;
            if (error) {
              setFormError(error);
              return;
            }
            setFormError(undefined);
            const answers = formStateToAnswerInstances(
              values,
              form.questionInstances,
            );
            submitAnswers({ form, answers });
            // dispatch(
            //   generateSubmitPolicyStreamFormAnswersThunk({ form, answers }),
            // );
            setOkToNavigate(true);
          }}
          onSubmitFailure={() => {
            if (requestedNavType === 'forward') {
              setRequestedNavType(undefined);
              setOkToNavigate(false);
            } else if (requestedNavType === 'backward') {
              setOkToNavigate(true);
            }
          }}
        >
          {() => (
            <>
              {/* <pre>initialValues: {JSON.stringify(initialValues, null, 4)}</pre> */}
              {/* <pre>{JSON.stringify(formState, null, 4)}</pre> */}
              {form.questionInstances.map(
                (questionInstance: QuoteWizardQuestionInstance) => (
                  <RenderedQuestionInstance
                    questionInstance={questionInstance}
                    key={questionInstance.id}
                    classes={classes}
                  />
                ),
              )}
              <FormErrorsWarning error={formError} />
              <div className={classes.navButtons}>
                <BackButton
                  type='submit'
                  onClick={() => {
                    setRequestedNavType('backward');
                  }}
                />
                <NextButton
                  type='submit'
                  onClick={() => {
                    setRequestedNavType('forward');
                  }}
                />
              </div>
            </>
          )}
        </Form>
      </PolicyStreamFormCard>
    );
  },
);
